import React from 'react';

import {
  Box,
  Text,
  VStack,
  HStack,
  Button,
  Icon,
  Alert,
  AlertIcon,
  List,
  ListItem,
  ListIcon,
} from '@chakra-ui/react';
import { FaPlay, FaStop, FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';

import { useColors } from './colors';

const ProcessControlPanel = ({
  isRunning,
  canStart,
  readinessItems = [],
  onStart,
  onStop,
  estimatedPartslinkCount,
  scopeCountDetail = null,
  scopeCountLoading = false,
}) => {
  const { textColor, backgroundCardColor, borderColor, secondaryTextColor } = useColors();

  const unmetItems = readinessItems.filter((item) => !item.met);
  const allMet = readinessItems.length > 0 && unmetItems.length === 0;

  return (
    <Box
      p={5}
      bg={backgroundCardColor}
      borderRadius="lg"
      borderWidth="1px"
      borderColor={borderColor}
      boxShadow="sm"
      height="100%"
      display="flex"
      flexDirection="column"
    >
      <Text color={textColor} fontSize="md" fontWeight="semibold" mb={1}>
        Main process
      </Text>
      <Text color={secondaryTextColor} fontSize="sm" mb={4}>
        Resolves partslinks to supplier SKUs, searches eBay for verified seller matches, fetches
        reference data, compiles the listing using your field rules, then saves to catalog and
        pushes to the marketplace.
      </Text>

      {estimatedPartslinkCount != null && estimatedPartslinkCount > 0 && (
        <Alert status="info" borderRadius="md" mb={4} fontSize="sm">
          <AlertIcon />
          <Box>
            <Text>
              Estimated scope:{' '}
              <strong>
                {scopeCountLoading ? '…' : estimatedPartslinkCount.toLocaleString()}
              </strong>{' '}
              partslink{estimatedPartslinkCount === 1 ? '' : 's'} to process
            </Text>
            {!scopeCountLoading && scopeCountDetail?.excludedExisting > 0 && (
              <Text fontSize="xs" color={secondaryTextColor} mt={1}>
                {scopeCountDetail.excludedExisting.toLocaleString()} already in catalog (excluded)
              </Text>
            )}
            {!scopeCountLoading && scopeCountDetail?.cappedByLimit && (
              <Text fontSize="xs" color={secondaryTextColor} mt={1}>
                Capped at {scopeCountDetail.limit?.toLocaleString()} per run limit
              </Text>
            )}
          </Box>
        </Alert>
      )}

      {readinessItems.length > 0 && !isRunning && (
        <Alert
          status={allMet ? 'success' : 'warning'}
          borderRadius="md"
          mb={4}
          alignItems="flex-start"
        >
          <AlertIcon mt={1} />
          <Box w="full">
            <Text fontSize="sm" fontWeight="semibold" mb={1}>
              {allMet ? 'Ready to start' : 'Before you can start'}
            </Text>
            <List spacing={1}>
              {readinessItems.map((item) => (
                <ListItem key={item.id} fontSize="sm" display="flex" alignItems="flex-start">
                  <ListIcon
                    as={item.met ? FaCheckCircle : FaExclamationCircle}
                    color={item.met ? 'green.500' : 'orange.500'}
                    mt={1}
                  />
                  <Text color={item.met ? 'green.700' : undefined}>{item.label}</Text>
                </ListItem>
              ))}
            </List>
          </Box>
        </Alert>
      )}

      <VStack spacing={3} mt="auto">
        {isRunning ? (
          <Button
            w="full"
            size="lg"
            colorScheme="red"
            leftIcon={<Icon as={FaStop} />}
            onClick={onStop}
          >
            Stop process
          </Button>
        ) : (
          <Button
            w="full"
            size="lg"
            colorScheme="teal"
            leftIcon={<Icon as={FaPlay} />}
            onClick={onStart}
            isDisabled={!canStart}
          >
            Start listing creation
          </Button>
        )}

        {canStart && !isRunning && (
          <HStack fontSize="xs" color="green.600" justify="center" w="full">
            <Icon as={FaCheckCircle} />
            <Text>All requirements met — ready to run</Text>
          </HStack>
        )}
      </VStack>
    </Box>
  );
};

export default ProcessControlPanel;
